import React, { useState, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { connect } from 'react-redux';
import { createSuperServiceableArea, getSuperServiceableArea, updateSuperServiceableArea, clearSuperServiceableArea } from '../../actions/superServiceableArea';
import { getInventoryNodes } from '../../actions/inventoryNode';
import { getSectors } from '../../actions/sector';
import { ArrowLeft, Save, MapPin, Map, Activity, FileText, Warehouse, Layers } from 'lucide-react';

const initialState = {
    name: '',
    description: '',
    city: '',
    state: '',
    sector: '',
    isActive: true,
    minNoticeHours: 1,
    maxDeliveriesPerHour: 10,
    boundary: {
        type: 'Polygon',
        coordinates: [[]]
    },
    microWarehouse: {
        inventoryNode: '',
        address: '',
        contactPerson: '',
        contactNumber: ''
    }
};

const SuperServiceableArea = ({
    createSuperServiceableArea,
    getSuperServiceableArea,
    updateSuperServiceableArea,
    clearSuperServiceableArea,
    getInventoryNodes,
    getSectors,
    superServiceableArea,
    inventoryNodes,
    sectors,
    loading
}) => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [formData, setFormData] = useState(initialState);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState({});
    const [coordinates, setCoordinates] = useState('');
    const [warehouseLat, setWarehouseLat] = useState('');
    const [warehouseLng, setWarehouseLng] = useState('');

    const isNewArea = id === 'new';

    useEffect(() => {
        getInventoryNodes();
        getSectors();
    }, [getInventoryNodes, getSectors]);

    useEffect(() => {
        document.title = isNewArea ? 'Create Super Serviceable Area' : 'Edit Super Serviceable Area';

        if (!isNewArea && (!superServiceableArea || superServiceableArea._id !== id)) {
            getSuperServiceableArea(id);
        } else if (!isNewArea && superServiceableArea && superServiceableArea._id === id) {
            setFormData({
                ...initialState,
                ...superServiceableArea,
                sector: superServiceableArea.sector?._id || superServiceableArea.sector || '',
                microWarehouse: {
                    ...initialState.microWarehouse,
                    ...superServiceableArea.microWarehouse,
                    inventoryNode: superServiceableArea.microWarehouse?.inventoryNode?._id
                        || superServiceableArea.microWarehouse?.inventoryNode
                        || ''
                }
            });

            // Convert coordinates to string format for editing
            if (superServiceableArea.boundary?.coordinates?.[0]) {
                const coordsString = superServiceableArea.boundary.coordinates[0]
                    .map(coord => `${coord[0]},${coord[1]}`)
                    .join(' ');
                setCoordinates(coordsString);
            }

            const whCoords = superServiceableArea.microWarehouse?.location?.coordinates;
            if (whCoords && whCoords.length === 2) {
                setWarehouseLng(String(whCoords[0]));
                setWarehouseLat(String(whCoords[1]));
            }
        } else if (isNewArea) {
            clearSuperServiceableArea();
            setFormData(initialState);
            setCoordinates('');
            setWarehouseLat('');
            setWarehouseLng('');
        }
    }, [id, isNewArea, superServiceableArea, getSuperServiceableArea, clearSuperServiceableArea]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;

        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }

        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleMicroWarehouseChange = (e) => {
        const { name, value } = e.target;

        if (errors[`microWarehouse.${name}`]) {
            setErrors(prev => ({ ...prev, [`microWarehouse.${name}`]: '' }));
        }

        setFormData(prev => ({
            ...prev,
            microWarehouse: {
                ...prev.microWarehouse,
                [name]: value
            }
        }));
    };

    const handleCoordinatesChange = (e) => {
        const value = e.target.value;
        setCoordinates(value);

        if (errors.coordinates) {
            setErrors(prev => ({ ...prev, coordinates: '' }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.name.trim()) {
            newErrors.name = 'Name is required';
        }

        if (!formData.city.trim()) {
            newErrors.city = 'City is required';
        }

        if (!formData.state.trim()) {
            newErrors.state = 'State is required';
        }

        if (!formData.sector) {
            newErrors.sector = 'Sector is required';
        }

        const minNotice = Number(formData.minNoticeHours);
        if (!Number.isFinite(minNotice) || minNotice < 1) {
            newErrors.minNoticeHours = 'Minimum notice must be at least 1 hour';
        }

        const maxPerHour = Number(formData.maxDeliveriesPerHour);
        if (!Number.isInteger(maxPerHour) || maxPerHour < 1) {
            newErrors.maxDeliveriesPerHour = 'Max deliveries per hour must be a whole number of at least 1';
        }

        if (!coordinates.trim()) {
            newErrors.coordinates = 'Boundary coordinates are required';
        } else {
            // Validate coordinate format
            const coordPairs = coordinates.trim().split(' ');
            if (coordPairs.length < 3) {
                newErrors.coordinates = 'At least 3 coordinate pairs are required for a polygon';
            } else {
                const validCoords = coordPairs.every(pair => {
                    const [lng, lat] = pair.split(',');
                    return !isNaN(parseFloat(lng)) && !isNaN(parseFloat(lat));
                });

                if (!validCoords) {
                    newErrors.coordinates = 'Invalid coordinate format. Use: lng,lat lng,lat lng,lat...';
                }
            }
        }

        if (!formData.microWarehouse.inventoryNode) {
            newErrors['microWarehouse.inventoryNode'] = 'Inventory node is required';
        }

        if (!formData.microWarehouse.address.trim()) {
            newErrors['microWarehouse.address'] = 'Micro warehouse address is required';
        }

        if (!warehouseLat.trim() || !warehouseLng.trim() || isNaN(parseFloat(warehouseLat)) || isNaN(parseFloat(warehouseLng))) {
            newErrors['microWarehouse.location'] = 'Valid micro warehouse latitude/longitude are required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const parseCoordinates = () => {
        const coordPairs = coordinates.trim().split(' ');
        const parsedCoords = coordPairs.map(pair => {
            const [lng, lat] = pair.split(',');
            return [parseFloat(lng), parseFloat(lat)];
        });

        // Ensure the polygon is closed (first and last coordinates are the same)
        if (parsedCoords.length > 0) {
            const first = parsedCoords[0];
            const last = parsedCoords[parsedCoords.length - 1];
            if (first[0] !== last[0] || first[1] !== last[1]) {
                parsedCoords.push([...first]);
            }
        }

        return [parsedCoords];
    };

    const onSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        try {
            setIsSubmitting(true);

            const submissionData = {
                ...formData,
                minNoticeHours: Number(formData.minNoticeHours),
                maxDeliveriesPerHour: Number(formData.maxDeliveriesPerHour),
                boundary: {
                    type: 'Polygon',
                    coordinates: parseCoordinates()
                },
                microWarehouse: {
                    ...formData.microWarehouse,
                    location: {
                        type: 'Point',
                        coordinates: [parseFloat(warehouseLng), parseFloat(warehouseLat)]
                    }
                }
            };

            if (isNewArea) {
                await createSuperServiceableArea(submissionData);
                navigate('/inventory/v1.2/super-serviceable-areas');
            } else {
                await updateSuperServiceableArea(superServiceableArea._id, submissionData);
            }
        } catch (error) {
            console.error('Error submitting form:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loading && !isNewArea) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <div className="text-center">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3 text-muted">Loading super serviceable area details...</p>
                </div>
            </div>
        );
    }

    return (
        <Fragment>
            <div className="container-fluid py-4">
                {/* Header */}
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center">
                            <Link
                                className="btn btn-outline-primary d-flex align-items-center"
                                to="/inventory/v1.2/super-serviceable-areas"
                            >
                                <ArrowLeft size={16} className="me-2" />
                                Back to Super Serviceable Areas
                            </Link>
                            <h2 className="text-primary mb-0 fw-bold">
                                {isNewArea ? 'Create Super Serviceable Area' : `Edit ${formData.name}`}
                            </h2>
                            <div></div>
                        </div>
                    </div>
                </div>

                <form onSubmit={onSubmit}>
                    <div className="row">
                        {/* Basic Information */}
                        <div className="col-lg-8">
                            <div className="card shadow-sm mb-4">
                                <div className="card-header bg-primary text-white">
                                    <h5 className="mb-0 d-flex align-items-center">
                                        <FileText size={20} className="me-2" />
                                        Basic Information
                                    </h5>
                                </div>
                                <div className="card-body">
                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-bold">Area Name *</label>
                                            <input
                                                type="text"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleChange}
                                                className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                                                placeholder="Enter super serviceable area name"
                                            />
                                            {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                                        </div>

                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-bold">Status</label>
                                            <div className="form-check form-switch">
                                                <input
                                                    type="checkbox"
                                                    name="isActive"
                                                    checked={formData.isActive}
                                                    onChange={handleChange}
                                                    className="form-check-input"
                                                    id="isActive"
                                                />
                                                <label className="form-check-label" htmlFor="isActive">
                                                    <Activity size={16} className="me-1" />
                                                    {formData.isActive ? 'Active' : 'Inactive'}
                                                </label>
                                            </div>
                                        </div>

                                        <div className="col-12 mb-3">
                                            <label className="form-label fw-bold">Description</label>
                                            <textarea
                                                name="description"
                                                value={formData.description}
                                                onChange={handleChange}
                                                className="form-control"
                                                placeholder="Enter area description (optional)"
                                                rows="3"
                                            />
                                        </div>

                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-bold">Minimum Notice (hours) *</label>
                                            <input
                                                type="number"
                                                name="minNoticeHours"
                                                min="1"
                                                step="0.5"
                                                value={formData.minNoticeHours}
                                                onChange={handleChange}
                                                className={`form-control ${errors.minNoticeHours ? 'is-invalid' : ''}`}
                                            />
                                            {errors.minNoticeHours && <div className="invalid-feedback">{errors.minNoticeHours}</div>}
                                            <div className="form-text">Customers cannot schedule a delivery sooner than this from now.</div>
                                        </div>

                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-bold">Max Deliveries Per Hour *</label>
                                            <input
                                                type="number"
                                                name="maxDeliveriesPerHour"
                                                min="1"
                                                step="1"
                                                value={formData.maxDeliveriesPerHour}
                                                onChange={handleChange}
                                                className={`form-control ${errors.maxDeliveriesPerHour ? 'is-invalid' : ''}`}
                                            />
                                            {errors.maxDeliveriesPerHour && <div className="invalid-feedback">{errors.maxDeliveriesPerHour}</div>}
                                            <div className="form-text">Scheduled deliveries accepted per clock hour (IST) in this area.</div>
                                        </div>

                                        <div className="col-12 mb-3">
                                            <label className="form-label fw-bold">Sector *</label>
                                            <div className="input-group">
                                                <span className="input-group-text">
                                                    <Layers size={16} />
                                                </span>
                                                <select
                                                    name="sector"
                                                    value={formData.sector}
                                                    onChange={handleChange}
                                                    className={`form-select ${errors.sector ? 'is-invalid' : ''}`}
                                                >
                                                    <option value="">Select a sector</option>
                                                    {sectors?.map(sector => (
                                                        <option key={sector._id} value={sector._id}>
                                                            {sector.serviceableArea?.name
                                                                ? `${sector.name} (${sector.serviceableArea.name})`
                                                                : sector.name}
                                                        </option>
                                                    ))}
                                                </select>
                                                {errors.sector && <div className="invalid-feedback">{errors.sector}</div>}
                                            </div>
                                            <div className="form-text">Every super serviceable area must belong to a sector.</div>
                                        </div>

                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-bold">City *</label>
                                            <div className="input-group">
                                                <span className="input-group-text">
                                                    <MapPin size={16} />
                                                </span>
                                                <input
                                                    type="text"
                                                    name="city"
                                                    value={formData.city}
                                                    onChange={handleChange}
                                                    className={`form-control ${errors.city ? 'is-invalid' : ''}`}
                                                    placeholder="Enter city name"
                                                />
                                                {errors.city && <div className="invalid-feedback">{errors.city}</div>}
                                            </div>
                                        </div>

                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-bold">State *</label>
                                            <div className="input-group">
                                                <span className="input-group-text">
                                                    <Map size={16} />
                                                </span>
                                                <input
                                                    type="text"
                                                    name="state"
                                                    value={formData.state}
                                                    onChange={handleChange}
                                                    className={`form-control ${errors.state ? 'is-invalid' : ''}`}
                                                    placeholder="Enter state name"
                                                />
                                                {errors.state && <div className="invalid-feedback">{errors.state}</div>}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Boundary Coordinates */}
                            <div className="card shadow-sm mb-4">
                                <div className="card-header bg-info text-white">
                                    <h5 className="mb-0 d-flex align-items-center">
                                        <Map size={20} className="me-2" />
                                        Boundary Coordinates
                                    </h5>
                                </div>
                                <div className="card-body">
                                    <div className="alert alert-info">
                                        <strong>Coordinate Format:</strong> Enter coordinates as space-separated pairs of longitude,latitude.
                                        <br />
                                        <strong>Example:</strong> 77.5946,12.9716 77.6146,12.9716 77.6146,12.9916 77.5946,12.9916
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label fw-bold">Polygon Coordinates *</label>
                                        <textarea
                                            value={coordinates}
                                            onChange={handleCoordinatesChange}
                                            className={`form-control ${errors.coordinates ? 'is-invalid' : ''}`}
                                            placeholder="Enter coordinates as: lng,lat lng,lat lng,lat..."
                                            rows="4"
                                            style={{ fontFamily: 'monospace' }}
                                        />
                                        {errors.coordinates && <div className="invalid-feedback">{errors.coordinates}</div>}
                                        <div className="form-text">
                                            Minimum 3 coordinate pairs required. The polygon will be automatically closed.
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Micro Warehouse */}
                            <div className="card shadow-sm mb-4">
                                <div className="card-header bg-secondary text-white">
                                    <h5 className="mb-0 d-flex align-items-center">
                                        <Warehouse size={20} className="me-2" />
                                        Micro Warehouse
                                    </h5>
                                </div>
                                <div className="card-body">
                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-bold">Inventory Node *</label>
                                            <select
                                                name="inventoryNode"
                                                value={formData.microWarehouse.inventoryNode}
                                                onChange={handleMicroWarehouseChange}
                                                className={`form-select ${errors['microWarehouse.inventoryNode'] ? 'is-invalid' : ''}`}
                                            >
                                                <option value="">Select an inventory node</option>
                                                {inventoryNodes?.map(node => (
                                                    <option key={node._id} value={node._id}>
                                                        {node.name}
                                                    </option>
                                                ))}
                                            </select>
                                            {errors['microWarehouse.inventoryNode'] && (
                                                <div className="invalid-feedback">{errors['microWarehouse.inventoryNode']}</div>
                                            )}
                                        </div>

                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-bold">Address *</label>
                                            <input
                                                type="text"
                                                name="address"
                                                value={formData.microWarehouse.address}
                                                onChange={handleMicroWarehouseChange}
                                                className={`form-control ${errors['microWarehouse.address'] ? 'is-invalid' : ''}`}
                                                placeholder="Enter micro warehouse address"
                                            />
                                            {errors['microWarehouse.address'] && (
                                                <div className="invalid-feedback">{errors['microWarehouse.address']}</div>
                                            )}
                                        </div>

                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-bold">Latitude *</label>
                                            <input
                                                type="text"
                                                value={warehouseLat}
                                                onChange={(e) => setWarehouseLat(e.target.value)}
                                                className={`form-control ${errors['microWarehouse.location'] ? 'is-invalid' : ''}`}
                                                placeholder="Enter latitude"
                                            />
                                        </div>

                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-bold">Longitude *</label>
                                            <input
                                                type="text"
                                                value={warehouseLng}
                                                onChange={(e) => setWarehouseLng(e.target.value)}
                                                className={`form-control ${errors['microWarehouse.location'] ? 'is-invalid' : ''}`}
                                                placeholder="Enter longitude"
                                            />
                                            {errors['microWarehouse.location'] && (
                                                <div className="invalid-feedback">{errors['microWarehouse.location']}</div>
                                            )}
                                        </div>

                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-bold">Contact Person</label>
                                            <input
                                                type="text"
                                                name="contactPerson"
                                                value={formData.microWarehouse.contactPerson}
                                                onChange={handleMicroWarehouseChange}
                                                className="form-control"
                                                placeholder="Enter contact person name (optional)"
                                            />
                                        </div>

                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-bold">Contact Number</label>
                                            <input
                                                type="text"
                                                name="contactNumber"
                                                value={formData.microWarehouse.contactNumber}
                                                onChange={handleMicroWarehouseChange}
                                                className="form-control"
                                                placeholder="Enter contact number (optional)"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Preview/Info Panel */}
                        <div className="col-lg-4">
                            <div className="card shadow-sm">
                                <div className="card-header bg-light">
                                    <h5 className="mb-0">Area Summary</h5>
                                </div>
                                <div className="card-body">
                                    <div className="mb-3">
                                        <strong>Name:</strong>
                                        <div className="text-muted">
                                            {formData.name || <em>Not specified</em>}
                                        </div>
                                    </div>

                                    <div className="mb-3">
                                        <strong>Location:</strong>
                                        <div className="text-muted">
                                            {formData.city && formData.state
                                                ? `${formData.city}, ${formData.state}`
                                                : <em>Not specified</em>
                                            }
                                        </div>
                                    </div>

                                    <div className="mb-3">
                                        <strong>Sector:</strong>
                                        <div className="text-muted">
                                            {sectors?.find(sector => sector._id === formData.sector)?.name || <em>Not specified</em>}
                                        </div>
                                    </div>

                                    <div className="mb-3">
                                        <strong>Status:</strong>
                                        <div>
                                            <span className={`badge bg-${formData.isActive ? 'success' : 'secondary'}`}>
                                                {formData.isActive ? 'Active' : 'Inactive'}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="mb-3">
                                        <strong>Boundary Points:</strong>
                                        <div className="text-muted">
                                            {coordinates.trim()
                                                ? `${coordinates.trim().split(' ').length} coordinates`
                                                : <em>No coordinates set</em>
                                            }
                                        </div>
                                    </div>

                                    {formData.description && (
                                        <div className="mb-3">
                                            <strong>Description:</strong>
                                            <div className="text-muted small">
                                                {formData.description}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Submit Button */}
                    <div className="row">
                        <div className="col-12">
                            <div className="d-flex justify-content-end gap-2">
                                <Link
                                    to="/inventory/v1.2/super-serviceable-areas"
                                    className="btn btn-outline-secondary"
                                >
                                    Cancel
                                </Link>
                                <button
                                    type="submit"
                                    className="btn btn-primary btn-lg"
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? (
                                        <>
                                            <div className="spinner-border spinner-border-sm me-2" role="status">
                                                <span className="visually-hidden">Loading...</span>
                                            </div>
                                            {isNewArea ? 'Creating...' : 'Updating...'}
                                        </>
                                    ) : (
                                        <>
                                            <Save size={16} className="me-2" />
                                            {isNewArea ? 'Create Super Serviceable Area' : 'Update Super Serviceable Area'}
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </Fragment>
    );
};

SuperServiceableArea.propTypes = {
    createSuperServiceableArea: PropTypes.func.isRequired,
    getSuperServiceableArea: PropTypes.func.isRequired,
    updateSuperServiceableArea: PropTypes.func.isRequired,
    clearSuperServiceableArea: PropTypes.func.isRequired,
    getInventoryNodes: PropTypes.func.isRequired,
    getSectors: PropTypes.func.isRequired,
    superServiceableArea: PropTypes.object,
    inventoryNodes: PropTypes.array,
    sectors: PropTypes.array,
    loading: PropTypes.bool.isRequired
};

const mapStateToProps = (state) => ({
    superServiceableArea: state.superServiceableArea.superServiceableArea,
    inventoryNodes: state.inventoryNode.inventoryNodes,
    sectors: state.sector.sectors,
    loading: state.superServiceableArea.loading
});

export default connect(mapStateToProps, {
    createSuperServiceableArea,
    getSuperServiceableArea,
    updateSuperServiceableArea,
    clearSuperServiceableArea,
    getInventoryNodes,
    getSectors
})(SuperServiceableArea);
